import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TableSortLabel,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Radio,
  RadioGroup,
  TablePagination,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  CloudUpload as ExportIcon,
  CloudDownload as ImportIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Inventory as PackageIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  AccountTree as ScenarioIcon,
  LinkOff as UnlinkedIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  CleaningServices as CleanupIcon,
} from '@mui/icons-material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { rangeosApiService, RangeOSScenario, OrphanedObjectsReport } from '../services/rangeos-api';
import { rangeosEnvironmentService, RangeOSEnvironment } from '../services/rangeos-environment-api';
import { basePrefixApi, EnvironmentWithBucket } from '../services/api';
import { scenarioValidationService, ScenarioValidationReport, ReferenceValidationDetail, SuggestedMatch } from '../services/scenario-validation-api';

interface Package {
  uuid: string;
  name: string;
  description?: string;
  author?: string;
  version?: string;
  dateCreated?: string;
  dateEdited?: string;
}

interface S3File {
  key: string;
  name: string;
  size: number;
  lastModified: string;
}

type ViewMode = 'by-scenario' | 'by-package';

interface SelectedTreeItem {
  type: 'scenario' | 'package';
  id: string;
}

export const PackageManager: React.FC = () => {
  // Environment state
  const [environments, setEnvironments] = useState<RangeOSEnvironment[]>([]);
  const [s3Environments, setS3Environments] = useState<EnvironmentWithBucket[]>([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
  const [selectedS3Environment, setSelectedS3Environment] = useState<string>('');

  // Tab state
  const [activeTab, setActiveTab] = useState<number>(0);

  // Data state
  const [packageList, setPackageList] = useState<Package[]>([]);
  const [scenarioList, setScenarioList] = useState<RangeOSScenario[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(new Set());
  const [selectedScenarios, setSelectedScenarios] = useState<Set<string>>(new Set());
  const [s3PackageFiles, setS3PackageFiles] = useState<S3File[]>([]);
  const [s3ScenarioFiles, setS3ScenarioFiles] = useState<S3File[]>([]);
  const [selectedS3PackageFiles, setSelectedS3PackageFiles] = useState<Set<string>>(new Set());
  const [selectedS3ScenarioFiles, setSelectedS3ScenarioFiles] = useState<Set<string>>(new Set());

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('by-scenario');
  const [selectedTreeItem, setSelectedTreeItem] = useState<SelectedTreeItem | null>(null);
  const [treeFilter, setTreeFilter] = useState<string>('');
  const [scenarioTreePage, setScenarioTreePage] = useState(0);
  const [scenarioTreeRowsPerPage, setScenarioTreeRowsPerPage] = useState(25);
  const [packageTreePage, setPackageTreePage] = useState(0);
  const [packageTreeRowsPerPage, setPackageTreeRowsPerPage] = useState(25);

  // Options
  const [includeDependencies, setIncludeDependencies] = useState<boolean>(true);
  const [includeReferencedPackages, setIncludeReferencedPackages] = useState<boolean>(false);
  const [importDependencies, setImportDependencies] = useState<boolean>(true);
  const [importReferencedPackages, setImportReferencedPackages] = useState<boolean>(false);

  // Loading state
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  // Export confirmation dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportTarget, setExportTarget] = useState<'s3' | 'local'>('s3');

  // Import confirmation dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importDialogType, setImportDialogType] = useState<'scenario' | 'package'>('scenario');
  const [importDialogSource, setImportDialogSource] = useState<'s3' | 'local'>('s3');
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);

  // Delete state
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteDependencies, setDeleteDependencies] = useState(true);
  const [deletePreview, setDeletePreview] = useState<Record<string, {
    packageName: string;
    willDelete: Array<{ fieldName: string; assetType: string; uuid: string; name?: string }>;
    willSkipShared: Array<{ fieldName: string; assetType: string; uuid: string; name?: string; sharedWith: string[] }>;
    totalDependencies: number;
  }>>({});
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Scenario delete state
  const [scenarioDeleteConfirmOpen, setScenarioDeleteConfirmOpen] = useState(false);
  const [scenarioDeleteOrphanedPackages, setScenarioDeleteOrphanedPackages] = useState(true);
  const [scenarioDeletePreview, setScenarioDeletePreview] = useState<Record<string, {
    scenarioName: string;
    packages: Array<{ uuid: string; name: string; isOrphaned: boolean; otherScenarios: string[] }>;
  }>>({});
  const [loadingScenarioDeletePreview, setLoadingScenarioDeletePreview] = useState(false);

  // Import validation state
  const [importValidationReport, setImportValidationReport] = useState<ScenarioValidationReport | null>(null);
  const [importUuidMappings, setImportUuidMappings] = useState<Map<string, { oldUuid: string; newUuid: string; type: string; resourceName?: string }>>(new Map());
  const [showImportValidationDialog, setShowImportValidationDialog] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<{ source: 's3' | 'local'; bundle: any; s3Key?: string; packageCount?: number } | null>(null);
  const [validatingBundle, setValidatingBundle] = useState(false);
  const [importMatchDialogOpen, setImportMatchDialogOpen] = useState(false);
  const [importCurrentMatch, setImportCurrentMatch] = useState<{ detail: ReferenceValidationDetail } | null>(null);
  const [importSelectedMatchUuid, setImportSelectedMatchUuid] = useState<string>('');
  const [importManualUuidInput, setImportManualUuidInput] = useState<string>('');
  const [importAvailableResources, setImportAvailableResources] = useState<any[]>([]);
  const [importLoadingResources, setImportLoadingResources] = useState(false);
  const [importResourceSearch, setImportResourceSearch] = useState<string>('');
  const [showAllResources, setShowAllResources] = useState(false);

  // Cleanup tab state
  const [orphanReport, setOrphanReport] = useState<OrphanedObjectsReport | null>(null);
  const [loadingOrphans, setLoadingOrphans] = useState(false);
  const [deepScan, setDeepScan] = useState(false);
  const [selectedOrphanScenarios, setSelectedOrphanScenarios] = useState<Set<string>>(new Set());
  const [selectedOrphanPackages, setSelectedOrphanPackages] = useState<Set<string>>(new Set());
  const [selectedOrphanAssets, setSelectedOrphanAssets] = useState<Map<string, Set<string>>>(new Map());
  const [deletingOrphans, setDeletingOrphans] = useState(false);
  const [orphanScenariosPage, setOrphanScenariosPage] = useState(0);
  const [orphanScenariosRowsPerPage, setOrphanScenariosRowsPerPage] = useState(25);
  const [orphanPackagesPage, setOrphanPackagesPage] = useState(0);
  const [orphanPackagesRowsPerPage, setOrphanPackagesRowsPerPage] = useState(25);
  const [orphanAssetPages, setOrphanAssetPages] = useState<Record<string, number>>({});
  const [orphanAssetRowsPerPage, setOrphanAssetRowsPerPage] = useState<Record<string, number>>({});

  // Messages
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // S3 import filter/sort/pagination
  const [s3PackageFilter, setS3PackageFilter] = useState<string>('');
  const [s3ScenarioFilter, setS3ScenarioFilter] = useState<string>('');
  const [s3PackageSortField, setS3PackageSortField] = useState<'name' | 'size' | 'lastModified'>('name');
  const [s3PackageSortDirection, setS3PackageSortDirection] = useState<'asc' | 'desc'>('asc');
  const [s3ScenarioSortField, setS3ScenarioSortField] = useState<'name' | 'size' | 'lastModified'>('name');
  const [s3ScenarioSortDirection, setS3ScenarioSortDirection] = useState<'asc' | 'desc'>('asc');
  const [s3ScenarioPage, setS3ScenarioPage] = useState(0);
  const [s3ScenarioRowsPerPage, setS3ScenarioRowsPerPage] = useState(25);
  const [s3PackagePage, setS3PackagePage] = useState(0);
  const [s3PackageRowsPerPage, setS3PackageRowsPerPage] = useState(25);

  // Load environments on mount
  useEffect(() => {
    loadEnvironments();
  }, []);

  // Load data when environment changes
  useEffect(() => {
    if (selectedEnvironment) {
      loadData();
    } else {
      setPackageList([]);
      setScenarioList([]);
    }
  }, [selectedEnvironment]);

  // Load S3 files when S3 environment changes
  useEffect(() => {
    if (selectedS3Environment) {
      loadS3Files();
    } else {
      setS3PackageFiles([]);
      setS3ScenarioFiles([]);
    }
  }, [selectedS3Environment]);

  const loadEnvironments = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rangeosEnvs, s3Envs] = await Promise.all([
        rangeosEnvironmentService.getActiveEnvironments(),
        basePrefixApi.getEnvironmentsWithBuckets()
      ]);
      setEnvironments(rangeosEnvs);
      setS3Environments(s3Envs);
    } catch (err: any) {
      setError(`Failed to load environments: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    if (!selectedEnvironment) return;
    setLoading(true);
    setError(null);
    try {
      const [packages, scenarios] = await Promise.all([
        rangeosApiService.listPackages(selectedEnvironment),
        rangeosApiService.listScenariosWithPackages(selectedEnvironment),
      ]);
      setPackageList(packages);
      setScenarioList(scenarios);
      setSelectedPackages(new Set());
      setSelectedScenarios(new Set());
      setSelectedTreeItem(null);
    } catch (err: any) {
      setError(`Failed to load data: ${err.message}`);
      setPackageList([]);
      setScenarioList([]);
    } finally {
      setLoading(false);
    }
  };

  const loadS3Files = async () => {
    if (!selectedS3Environment) return;
    setLoading(true);
    setError(null);
    try {
      const [pkgFiles, scenFiles] = await Promise.all([
        rangeosApiService.listPackageFilesFromS3(selectedS3Environment),
        rangeosApiService.listScenarioFilesFromS3(selectedS3Environment),
      ]);
      setS3PackageFiles(pkgFiles);
      setS3ScenarioFiles(scenFiles);
      setSelectedS3PackageFiles(new Set());
      setSelectedS3ScenarioFiles(new Set());
    } catch (err: any) {
      setError(`Failed to load S3 files: ${err.message}`);
      setS3PackageFiles([]);
      setS3ScenarioFiles([]);
    } finally {
      setLoading(false);
    }
  };

  // Relationship maps
  const scenarioToPackages = useMemo(() => {
    const map = new Map<string, Package[]>();
    for (const scenario of scenarioList) {
      if (scenario.packages && scenario.packages.length > 0) {
        const pkgs = scenario.packages
          .map(uuid => packageList.find(p => p.uuid === uuid))
          .filter((p): p is Package => !!p);
        map.set(scenario.id, pkgs);
      } else {
        map.set(scenario.id, []);
      }
    }
    return map;
  }, [scenarioList, packageList]);

  const packageToScenarios = useMemo(() => {
    const map = new Map<string, RangeOSScenario[]>();
    for (const pkg of packageList) {
      const refs = scenarioList.filter(s => s.packages?.includes(pkg.uuid));
      map.set(pkg.uuid, refs);
    }
    return map;
  }, [scenarioList, packageList]);

  const unlinkedPackages = useMemo(() => {
    return packageList.filter(pkg => {
      const refs = packageToScenarios.get(pkg.uuid);
      return !refs || refs.length === 0;
    });
  }, [packageList, packageToScenarios]);

  // Filtered items based on tree filter
  const filteredScenarios = useMemo(() => {
    if (!treeFilter) return scenarioList;
    const lower = treeFilter.toLowerCase();
    return scenarioList.filter(s =>
      s.name?.toLowerCase().includes(lower) ||
      s.description?.toLowerCase().includes(lower) ||
      s.author?.toLowerCase().includes(lower) ||
      s.id?.toLowerCase().includes(lower)
    );
  }, [scenarioList, treeFilter]);

  const filteredPackages = useMemo(() => {
    if (!treeFilter) return packageList;
    const lower = treeFilter.toLowerCase();
    return packageList.filter(p =>
      p.name?.toLowerCase().includes(lower) ||
      p.description?.toLowerCase().includes(lower) ||
      p.author?.toLowerCase().includes(lower) ||
      p.uuid?.toLowerCase().includes(lower)
    );
  }, [packageList, treeFilter]);

  const filteredUnlinkedPackages = useMemo(() => {
    if (!treeFilter) return unlinkedPackages;
    const lower = treeFilter.toLowerCase();
    return unlinkedPackages.filter(p =>
      p.name?.toLowerCase().includes(lower) ||
      p.description?.toLowerCase().includes(lower) ||
      p.author?.toLowerCase().includes(lower) ||
      p.uuid?.toLowerCase().includes(lower)
    );
  }, [unlinkedPackages, treeFilter]);

  // S3 file filtering/sorting
  const filteredS3PackageFiles = useMemo(() => {
    let filtered = s3PackageFiles;
    if (s3PackageFilter) {
      const lower = s3PackageFilter.toLowerCase();
      filtered = filtered.filter(f => f.name?.toLowerCase().includes(lower) || f.key?.toLowerCase().includes(lower));
    }
    return [...filtered].sort((a, b) => {
      if (s3PackageSortField === 'name') {
        const cmp = (a.name || '').localeCompare(b.name || '');
        return s3PackageSortDirection === 'asc' ? cmp : -cmp;
      } else if (s3PackageSortField === 'size') {
        return s3PackageSortDirection === 'asc' ? a.size - b.size : b.size - a.size;
      } else {
        const aD = new Date(a.lastModified).getTime();
        const bD = new Date(b.lastModified).getTime();
        return s3PackageSortDirection === 'asc' ? aD - bD : bD - aD;
      }
    });
  }, [s3PackageFiles, s3PackageFilter, s3PackageSortField, s3PackageSortDirection]);

  const filteredS3ScenarioFiles = useMemo(() => {
    let filtered = s3ScenarioFiles;
    if (s3ScenarioFilter) {
      const lower = s3ScenarioFilter.toLowerCase();
      filtered = filtered.filter(f => f.name?.toLowerCase().includes(lower) || f.key?.toLowerCase().includes(lower));
    }
    return [...filtered].sort((a, b) => {
      if (s3ScenarioSortField === 'name') {
        const cmp = (a.name || '').localeCompare(b.name || '');
        return s3ScenarioSortDirection === 'asc' ? cmp : -cmp;
      } else if (s3ScenarioSortField === 'size') {
        return s3ScenarioSortDirection === 'asc' ? a.size - b.size : b.size - a.size;
      } else {
        const aD = new Date(a.lastModified).getTime();
        const bD = new Date(b.lastModified).getTime();
        return s3ScenarioSortDirection === 'asc' ? aD - bD : bD - aD;
      }
    });
  }, [s3ScenarioFiles, s3ScenarioFilter, s3ScenarioSortField, s3ScenarioSortDirection]);

  // Selection handlers
  const toggleScenarioSelection = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newSelection = new Set(selectedScenarios);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedScenarios(newSelection);
  };

  const togglePackageSelection = (uuid: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newSelection = new Set(selectedPackages);
    if (newSelection.has(uuid)) {
      newSelection.delete(uuid);
    } else {
      newSelection.add(uuid);
    }
    setSelectedPackages(newSelection);
  };

  const toggleS3PackageFileSelection = (key: string) => {
    const newSelection = new Set(selectedS3PackageFiles);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedS3PackageFiles(newSelection);
  };

  const toggleS3ScenarioFileSelection = (key: string) => {
    const newSelection = new Set(selectedS3ScenarioFiles);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedS3ScenarioFiles(newSelection);
  };

  // Export handlers
  const confirmExport = () => {
    setExportDialogOpen(false);
    if (exportTarget === 's3') {
      handleExportToS3();
    } else {
      handleExportToLocal();
    }
  };

  const confirmImport = () => {
    setImportDialogOpen(false);
    if (importDialogType === 'scenario') {
      if (importDialogSource === 's3') {
        handleImportScenariosFromS3();
      } else if (pendingImportFile) {
        const file = pendingImportFile;
        setPendingImportFile(null);
        file.text().then(content => {
          const data = JSON.parse(content);
          validateAndImportScenario('local', data);
        }).catch(err => {
          setError(`Import failed: ${err.message}`);
        });
      }
    } else {
      if (importDialogSource === 's3') {
        handleImportPackagesFromS3();
      } else if (pendingImportFile) {
        const file = pendingImportFile;
        setPendingImportFile(null);
        setImporting(true);
        setError(null);
        setSuccess(null);
        file.text().then(content => {
          const data = JSON.parse(content);
          return rangeosApiService.importPackageFromJson(selectedEnvironment, data, importDependencies);
        }).then((result) => {
          const depCount = result.importedDependencies ? Object.keys(result.importedDependencies).length : 0;
          const depMsg = depCount > 0 ? ` (with ${depCount} dependency type(s))` : '';
          setSuccess(`Imported package from ${file.name}${depMsg}`);
          loadData();
        }).catch(err => {
          setError(`Import failed: ${err.message}`);
        }).finally(() => {
          setImporting(false);
        });
      }
    }
  };

  const handleExportToS3 = async () => {
    if (!selectedS3Environment) {
      setError('Please select an S3 destination');
      return;
    }

    setExporting(true);
    setError(null);
    setSuccess(null);

    const results: string[] = [];
    const errors: string[] = [];

    try {
      // Export selected scenarios
      if (selectedScenarios.size > 0) {
        const result = await rangeosApiService.batchExportScenariosToS3(
          selectedEnvironment,
          {
            targetEnvironment: selectedS3Environment,
            uuids: Array.from(selectedScenarios),
            includeReferencedPackages,
          }
        );
        if (result.exported.length > 0) results.push(`${result.exported.length} scenario(s)`);
        if (result.failed.length > 0) errors.push(`${result.failed.length} scenario(s) failed`);
      }

      // Export selected packages
      if (selectedPackages.size > 0) {
        const result = await rangeosApiService.batchExportPackagesToS3(
          selectedEnvironment,
          {
            targetEnvironment: selectedS3Environment,
            uuids: Array.from(selectedPackages),
            includeDependencies,
          }
        );
        if (result.exported.length > 0) results.push(`${result.exported.length} package(s)`);
        if (result.failed.length > 0) errors.push(`${result.failed.length} package(s) failed`);
      }

      if (results.length > 0) {
        setSuccess(`Exported ${results.join(' and ')} to S3`);
        setSelectedScenarios(new Set());
        setSelectedPackages(new Set());
        loadS3Files();
      }
      if (errors.length > 0) {
        setError(errors.join(', '));
      }
    } catch (err: any) {
      setError(`Export failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportToLocal = async () => {
    setExporting(true);
    setError(null);
    setSuccess(null);

    try {
      let count = 0;

      // Download selected scenarios
      for (const id of Array.from(selectedScenarios)) {
        const bundle = await rangeosApiService.exportScenarioBundle(selectedEnvironment, id, includeReferencedPackages);
        const scenario = scenarioList.find(s => s.id === id);
        const filename = `${(scenario?.name || id).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_scenario_${Date.now()}.json`;
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        count++;
      }

      // Download selected packages
      for (const uuid of Array.from(selectedPackages)) {
        const bundle = await rangeosApiService.exportPackageBundle(selectedEnvironment, uuid, includeDependencies);
        const pkg = packageList.find(p => p.uuid === uuid);
        const filename = `${(pkg?.name || uuid).replace(/[^a-z0-9]/gi, '_').toLowerCase()}_package_${Date.now()}.json`;
        const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        count++;
      }

      setSuccess(`Downloaded ${count} file(s)`);
    } catch (err: any) {
      setError(`Download failed: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Import handlers
  const handleImportScenariosFromS3 = async () => {
    if (selectedS3ScenarioFiles.size === 0 || !selectedEnvironment) {
      setError('Please select scenario files and a target environment');
      return;
    }

    const s3Keys = Array.from(selectedS3ScenarioFiles);

    // For single scenario, validate first
    if (s3Keys.length === 1) {
      setValidatingBundle(true);
      setError(null);
      setSuccess(null);
      try {
        const report = await scenarioValidationService.validateS3Bundle(selectedEnvironment, s3Keys[0]);

        if (report.overallValid) {
          // No missing refs — import directly
          setValidatingBundle(false);
          setImporting(true);
          try {
            const result = await rangeosApiService.importScenarioFromS3(selectedEnvironment, {
              s3Key: s3Keys[0],
              importReferencedPackages,
            });
            setSuccess(`Imported scenario "${result.scenario?.name || 'unnamed'}" successfully`);
            setSelectedS3ScenarioFiles(new Set());
            loadData();
          } finally {
            setImporting(false);
          }
          return;
        } else {
          // Show validation dialog
          setImportValidationReport(report);
          setImportUuidMappings(new Map());
          setPendingImportData({ source: 's3', bundle: null, s3Key: s3Keys[0], packageCount: (report as any).bundlePackageCount });
          setShowImportValidationDialog(true);
          setSelectedS3ScenarioFiles(new Set());
          return;
        }
      } catch (err: any) {
        console.warn('Validation failed, falling back to direct import:', err.message);
      } finally {
        setValidatingBundle(false);
      }
    }

    // For multiple files or if validation fails, use batch import directly
    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await rangeosApiService.batchImportScenariosFromS3(
        selectedEnvironment,
        { s3Keys, importReferencedPackages }
      );

      if (result.success) {
        setSuccess(`Imported ${result.imported.length} scenario(s)`);
        setSelectedS3ScenarioFiles(new Set());
        loadData();
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleImportPackagesFromS3 = async () => {
    if (selectedS3PackageFiles.size === 0 || !selectedEnvironment) {
      setError('Please select package files and a target environment');
      return;
    }

    setImporting(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await rangeosApiService.batchImportPackagesFromS3(
        selectedEnvironment,
        { s3Keys: Array.from(selectedS3PackageFiles), importDependencies }
      );

      if (result.success) {
        setSuccess(`Imported ${result.imported.length} package(s)`);
        setSelectedS3PackageFiles(new Set());
        loadData();
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // Import validation helpers
  const validateAndImportScenario = async (source: 's3' | 'local', bundle: any, s3Key?: string) => {
    if (!selectedEnvironment) return;

    setValidatingBundle(true);
    setError(null);
    setSuccess(null);

    try {
      const report = await scenarioValidationService.validateScenarioBundle(selectedEnvironment, bundle);

      if (report.overallValid) {
        // No missing references — import directly
        await executeScenarioImport(source, bundle, s3Key);
      } else {
        // Has missing references — show validation dialog
        setImportValidationReport(report);
        setImportUuidMappings(new Map());
        setPendingImportData({ source, bundle, s3Key, packageCount: bundle?.packages?.length });
        setShowImportValidationDialog(true);
      }
    } catch (err: any) {
      setError(`Validation failed: ${err.message}`);
    } finally {
      setValidatingBundle(false);
    }
  };

  const executeScenarioImport = async (source: 's3' | 'local', bundle: any, s3Key?: string, uuidMappings?: Array<{ oldUuid: string; newUuid: string }>) => {
    if (!selectedEnvironment) return;

    setImporting(true);
    setError(null);

    try {
      let result;
      if (source === 's3' && s3Key) {
        result = await rangeosApiService.importScenarioFromS3(selectedEnvironment, {
          s3Key,
          importReferencedPackages,
          uuidMappings,
        });
      } else {
        result = await rangeosApiService.importScenarioFromJson(
          selectedEnvironment,
          bundle,
          importReferencedPackages,
          uuidMappings,
        );
      }
      setSuccess(`Imported scenario "${result.scenario?.name || 'unnamed'}" successfully`);
      loadData();
    } catch (err: any) {
      setError(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleImportWithMappings = async () => {
    if (!pendingImportData) return;
    const mappingsArray = Array.from(importUuidMappings.values()).map(m => ({ oldUuid: m.oldUuid, newUuid: m.newUuid }));
    setShowImportValidationDialog(false);
    await executeScenarioImport(pendingImportData.source, pendingImportData.bundle, pendingImportData.s3Key, mappingsArray);
    setPendingImportData(null);
    setImportValidationReport(null);
    setImportUuidMappings(new Map());
  };

  const handleImportAnyway = async () => {
    if (!pendingImportData) return;
    setShowImportValidationDialog(false);
    await executeScenarioImport(pendingImportData.source, pendingImportData.bundle, pendingImportData.s3Key);
    setPendingImportData(null);
    setImportValidationReport(null);
    setImportUuidMappings(new Map());
  };

  const autoMapHighConfidenceImport = () => {
    if (!importValidationReport) return;
    const newMappings = new Map(importUuidMappings);
    let matchCount = 0;

    Object.values(importValidationReport.details.byType).forEach((typeDetails) => {
      typeDetails.references.forEach((detail: ReferenceValidationDetail) => {
        if (detail.exists || newMappings.has(detail.uuid)) return;
        const highMatch = detail.suggestedMatches?.find(m => m.confidence === 'high');
        if (highMatch) {
          newMappings.set(detail.uuid, {
            oldUuid: detail.uuid,
            newUuid: highMatch.uuid,
            type: detail.type,
            resourceName: highMatch.name,
          });
          matchCount++;
        }
      });
    });

    setImportUuidMappings(newMappings);
    if (matchCount > 0) {
      setSuccess(`Auto-mapped ${matchCount} high confidence match(es)`);
    } else {
      setError('No high confidence matches found to auto-map');
    }
  };

  const openImportMatchDialog = async (detail: ReferenceValidationDetail) => {
    setImportCurrentMatch({ detail });
    setImportSelectedMatchUuid(detail.suggestedMatches?.[0]?.uuid || '');
    setImportManualUuidInput('');
    setImportResourceSearch('');
    setShowAllResources(!detail.suggestedMatches || detail.suggestedMatches.length === 0);
    setImportMatchDialogOpen(true);
    // Always load available resources for the search/browse feature
    await loadImportAvailableResources(detail.type);
  };

  const loadImportAvailableResources = async (resourceType: string) => {
    if (!selectedEnvironment) return;
    setImportLoadingResources(true);
    setImportAvailableResources([]);
    try {
      let resources: any[] = [];
      switch (resourceType.toUpperCase()) {
        case 'VMIMAGE': resources = await rangeosApiService.listVMImages(selectedEnvironment); break;
        case 'ANSIBLEROLE': resources = await rangeosApiService.listAnsibleRoles(selectedEnvironment); break;
        case 'RANGEL3NETWORK': resources = await rangeosApiService.listRangeL3Networks(selectedEnvironment); break;
        case 'RANGENETWORK': resources = await rangeosApiService.listRangeNetworks(selectedEnvironment); break;
        case 'CHART': resources = await rangeosApiService.listCharts(selectedEnvironment); break;
        case 'GHOSTTRAFFIC': resources = await rangeosApiService.listGhostTraffic(selectedEnvironment); break;
        case 'PACKAGE': resources = await rangeosApiService.listPackages(selectedEnvironment); break;
      }
      setImportAvailableResources(resources);
    } catch (err: any) {
      console.error(`Failed to load ${resourceType} resources:`, err);
    } finally {
      setImportLoadingResources(false);
    }
  };

  const confirmImportMatch = () => {
    const uuidToUse = importSelectedMatchUuid || importManualUuidInput.trim();
    if (!importCurrentMatch || !uuidToUse) return;

    const { detail } = importCurrentMatch;
    const selectedMatch = detail.suggestedMatches?.find(m => m.uuid === uuidToUse);
    const selectedResource = importAvailableResources.find(r => r.uuid === uuidToUse);

    const newMappings = new Map(importUuidMappings);
    newMappings.set(detail.uuid, {
      oldUuid: detail.uuid,
      newUuid: uuidToUse,
      type: detail.type,
      resourceName: selectedMatch?.name || selectedResource?.name || 'Manual mapping',
    });
    setImportUuidMappings(newMappings);

    setImportMatchDialogOpen(false);
    setImportCurrentMatch(null);
    setImportSelectedMatchUuid('');
    setImportManualUuidInput('');
    setImportResourceSearch('');
    setShowAllResources(false);
    setImportAvailableResources([]);
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  // Delete handler (packages only)
  const handleDeletePackages = async () => {
    if (selectedPackages.size === 0 || !selectedEnvironment) return;

    setDeleting(true);
    setDeleteConfirmOpen(false);
    setError(null);
    setSuccess(null);

    const results: string[] = [];
    const errors: string[] = [];

    for (const uuid of Array.from(selectedPackages)) {
      const pkg = packageList.find(p => p.uuid === uuid);
      const pkgName = pkg?.name || uuid;

      try {
        if (deleteDependencies) {
          const result = await rangeosApiService.deletePackageWithDependencies(selectedEnvironment, uuid);
          const parts = [`Deleted "${pkgName}"`];
          if (result.deletedDependencies.length > 0) parts.push(`${result.deletedDependencies.length} dependencies removed`);
          if (result.skippedShared.length > 0) parts.push(`${result.skippedShared.length} shared deps preserved`);
          if (result.failedDeletions.length > 0) parts.push(`${result.failedDeletions.length} failed`);
          results.push(parts.join(', '));
        } else {
          await rangeosApiService.deletePackage(selectedEnvironment, uuid);
          results.push(`Deleted "${pkgName}"`);
        }
      } catch (err: any) {
        errors.push(`Failed to delete "${pkgName}": ${err.message}`);
      }
    }

    if (results.length > 0) setSuccess(results.join(' | '));
    if (errors.length > 0) setError(errors.join(' | '));

    setSelectedPackages(new Set());
    setDeleting(false);
    loadData();
  };

  // Delete handler (scenarios)
  const handleDeleteScenarios = async () => {
    if (selectedScenarios.size === 0 || !selectedEnvironment) return;

    setDeleting(true);
    setScenarioDeleteConfirmOpen(false);
    setError(null);
    setSuccess(null);

    const results: string[] = [];
    const errors: string[] = [];

    for (const uuid of Array.from(selectedScenarios)) {
      const scenario = scenarioList.find((s: any) => s.id === uuid || s.uuid === uuid);
      const scenarioName = scenario?.name || uuid;

      try {
        const result = await rangeosApiService.deleteScenario(selectedEnvironment, uuid, scenarioDeleteOrphanedPackages);
        const parts = [`Deleted "${scenarioName}"`];
        if (result.deletedPackages.length > 0) parts.push(`${result.deletedPackages.length} orphaned packages removed`);
        if (result.skippedPackages.length > 0) parts.push(`${result.skippedPackages.length} shared packages preserved`);
        if (result.failedPackages.length > 0) parts.push(`${result.failedPackages.length} package deletions failed`);
        results.push(parts.join(', '));
      } catch (err: any) {
        errors.push(`Failed to delete "${scenarioName}": ${err.message}`);
      }
    }

    if (results.length > 0) setSuccess(results.join(' | '));
    if (errors.length > 0) setError(errors.join(' | '));

    setSelectedScenarios(new Set());
    setDeleting(false);
    setScenarioDeletePreview({});
    loadData();
  };

  const openScenarioDeleteConfirm = async () => {
    setScenarioDeleteConfirmOpen(true);
    setLoadingScenarioDeletePreview(true);
    setScenarioDeletePreview({});

    const previews: typeof scenarioDeletePreview = {};
    for (const uuid of Array.from(selectedScenarios)) {
      try {
        const preview = await rangeosApiService.previewDeleteScenario(selectedEnvironment!, uuid);
        previews[uuid] = preview;
      } catch (err: any) {
        console.warn(`Failed to load delete preview for scenario ${uuid}:`, err.message);
      }
    }
    setScenarioDeletePreview(previews);
    setLoadingScenarioDeletePreview(false);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // --- Cleanup tab handlers ---

  const scanForOrphans = async () => {
    if (!selectedEnvironment) return;
    setLoadingOrphans(true);
    setError(null);
    setOrphanReport(null);
    setSelectedOrphanScenarios(new Set());
    setSelectedOrphanPackages(new Set());
    setSelectedOrphanAssets(new Map());
    setOrphanScenariosPage(0);
    setOrphanPackagesPage(0);
    setOrphanAssetPages({});
    setOrphanAssetRowsPerPage({});
    try {
      const report = await rangeosApiService.findOrphanedObjects(selectedEnvironment, deepScan);
      setOrphanReport(report);
    } catch (err: any) {
      setError(`Failed to scan for orphans: ${err.message}`);
    } finally {
      setLoadingOrphans(false);
    }
  };

  const toggleOrphanAsset = (assetType: string, uuid: string) => {
    setSelectedOrphanAssets(prev => {
      const next = new Map(prev);
      const set = new Set(next.get(assetType) || []);
      if (set.has(uuid)) set.delete(uuid); else set.add(uuid);
      if (set.size === 0) next.delete(assetType); else next.set(assetType, set);
      return next;
    });
  };

  const toggleAllOrphanAssets = (assetType: string, uuids: string[]) => {
    setSelectedOrphanAssets(prev => {
      const next = new Map(prev);
      const existing = next.get(assetType);
      if (existing && existing.size === uuids.length) {
        next.delete(assetType);
      } else {
        next.set(assetType, new Set(uuids));
      }
      return next;
    });
  };

  const totalSelectedOrphans = selectedOrphanScenarios.size + selectedOrphanPackages.size +
    Array.from(selectedOrphanAssets.values()).reduce((sum, set) => sum + set.size, 0);

  const deleteSelectedOrphans = async () => {
    if (!selectedEnvironment || totalSelectedOrphans === 0) return;
    setDeletingOrphans(true);
    setError(null);
    let deleted = 0;
    let failed = 0;

    // Delete orphaned scenarios
    for (const uuid of Array.from(selectedOrphanScenarios)) {
      try {
        await rangeosApiService.deleteScenario(selectedEnvironment, uuid, false);
        deleted++;
      } catch { failed++; }
    }

    // Delete orphaned packages (with dependencies)
    for (const uuid of Array.from(selectedOrphanPackages)) {
      try {
        await rangeosApiService.deletePackageWithDependencies(selectedEnvironment, uuid);
        deleted++;
      } catch { failed++; }
    }

    // Delete orphaned assets
    for (const [assetType, uuids] of Array.from(selectedOrphanAssets)) {
      for (const uuid of Array.from(uuids)) {
        try {
          await rangeosApiService.deleteContentAsset(selectedEnvironment, assetType, uuid);
          deleted++;
        } catch { failed++; }
      }
    }

    setDeletingOrphans(false);
    setSuccess(`Deleted ${deleted} orphaned object(s)${failed > 0 ? `, ${failed} failed` : ''}`);

    // Re-scan to refresh
    scanForOrphans();
    // Also refresh main data
    loadData();
  };

  const ASSET_TYPE_LABELS: Record<string, string> = {
    vmSpecifications: 'VM Specifications',
    containerSpecifications: 'Container Specifications',
    ghostAgents: 'Ghost Agents',
    ghostC2Servers: 'Ghost C2 Servers',
    ghostClients: 'Ghost Clients',
    ghostTrafficProfiles: 'Ghost Traffic Profiles',
    internetGateways: 'Internet Gateways',
    rangeBGPs: 'Range BGPs',
    rangeBGPLinks: 'Range BGP Links',
    rangeNetworks: 'Range Networks',
    rangeHostNetworks: 'Range Host Networks',
    rangeRouters: 'Range Routers',
    rangeTorNets: 'Range Tor Nets',
    rangeAutoL3Networks: 'Range Auto L3 Networks',
    rangeL3Networks: 'Range L3 Networks',
    rangeAutoIPs: 'Range Auto IPs',
    rangeIPs: 'Range IPs',
    rangeDNSServers: 'Range DNS Servers',
    rangeDNSZones: 'Range DNS Zones',
    rangeDNSRecords: 'Range DNS Records',
    rangeCerts: 'Range Certificates',
    rangePkis: 'Range PKIs',
    rangeConsoles: 'Range Consoles',
    rangeVolumes: 'Range Volumes',
    ansiblePlaybooks: 'Ansible Playbooks',
    telemetryAgents: 'Telemetry Agents',
    scenarioGroups: 'Scenario Groups',
  };

  const totalSelected = selectedScenarios.size + selectedPackages.size;

  // Detail panel content
  const renderDetailPanel = () => {
    if (!selectedTreeItem) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
          <Typography variant="body2">Select an item to view details</Typography>
        </Box>
      );
    }

    if (selectedTreeItem.type === 'scenario') {
      const scenario = scenarioList.find(s => s.id === selectedTreeItem.id);
      if (!scenario) return null;
      const pkgs = scenarioToPackages.get(scenario.id) || [];

      return (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ScenarioIcon color="primary" />
            <Typography variant="h6">{scenario.name}</Typography>
          </Box>
          {scenario.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{scenario.description}</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {scenario.author && <Chip label={`Author: ${scenario.author}`} size="small" variant="outlined" />}
            {scenario.version && <Chip label={`v${scenario.version}`} size="small" variant="outlined" />}
            {scenario.drafts && scenario.drafts.length > 0 && (
              <Chip label={`${scenario.drafts.length} draft(s)`} size="small" color="info" variant="outlined" />
            )}
            {(!scenario.drafts || scenario.drafts.length === 0) && (
              <Chip label="No draft" size="small" variant="outlined" />
            )}
          </Box>
          {scenario.dateCreated && (
            <Typography variant="caption" color="text.secondary" display="block">
              Created: {new Date(scenario.dateCreated).toLocaleString()}
            </Typography>
          )}
          {scenario.dateEdited && (
            <Typography variant="caption" color="text.secondary" display="block">
              Edited: {new Date(scenario.dateEdited).toLocaleString()}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', mt: 1 }}>
            UUID: {scenario.id}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Packages ({pkgs.length})
          </Typography>
          {pkgs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No packages referenced</Typography>
          ) : (
            <List dense disablePadding>
              {pkgs.map(pkg => (
                <ListItem
                  key={pkg.uuid}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                  onClick={() => setSelectedTreeItem({ type: 'package', id: pkg.uuid })}
                >
                  <PackageIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText
                    primary={pkg.name}
                    secondary={pkg.version ? `v${pkg.version}` : undefined}
                  />
                  <ChevronRightIcon fontSize="small" color="action" />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      );
    }

    if (selectedTreeItem.type === 'package') {
      const pkg = packageList.find(p => p.uuid === selectedTreeItem.id);
      if (!pkg) return null;
      const refs = packageToScenarios.get(pkg.uuid) || [];

      return (
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PackageIcon color="primary" />
            <Typography variant="h6">{pkg.name}</Typography>
          </Box>
          {pkg.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{pkg.description}</Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {pkg.author && <Chip label={`Author: ${pkg.author}`} size="small" variant="outlined" />}
            {pkg.version && <Chip label={`v${pkg.version}`} size="small" variant="outlined" />}
          </Box>
          {pkg.dateCreated && (
            <Typography variant="caption" color="text.secondary" display="block">
              Created: {new Date(pkg.dateCreated).toLocaleString()}
            </Typography>
          )}
          {pkg.dateEdited && (
            <Typography variant="caption" color="text.secondary" display="block">
              Edited: {new Date(pkg.dateEdited).toLocaleString()}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', mt: 1 }}>
            UUID: {pkg.uuid}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Referenced by Scenarios ({refs.length})
          </Typography>
          {refs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Not referenced by any scenario</Typography>
          ) : (
            <List dense disablePadding>
              {refs.map(scenario => (
                <ListItem
                  key={scenario.id}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1 }}
                  onClick={() => setSelectedTreeItem({ type: 'scenario', id: scenario.id })}
                >
                  <ScenarioIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText primary={scenario.name} />
                  <ChevronRightIcon fontSize="small" color="action" />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      );
    }

    return null;
  };

  // Render tree items
  const renderByScenarioTree = () => {
    const paginatedScenarios = filteredScenarios.slice(
      scenarioTreePage * scenarioTreeRowsPerPage,
      scenarioTreePage * scenarioTreeRowsPerPage + scenarioTreeRowsPerPage
    );
    return (
      <>
        <SimpleTreeView>
          {paginatedScenarios.map(scenario => {
            const pkgs = scenarioToPackages.get(scenario.id) || [];
            return (
              <TreeItem
                key={`scenario-${scenario.id}`}
                itemId={`scenario-${scenario.id}`}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={selectedScenarios.has(scenario.id)}
                      onClick={(e) => toggleScenarioSelection(scenario.id, e)}
                      sx={{ p: 0.5, mr: 0.5 }}
                    />
                    <ScenarioIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {scenario.name}
                    </Typography>
                    {pkgs.length > 0 && (
                      <Chip label={`${pkgs.length} pkg`} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                }
                onClick={() => setSelectedTreeItem({ type: 'scenario', id: scenario.id })}
              >
                {pkgs.map(pkg => (
                  <TreeItem
                    key={`scenario-${scenario.id}-pkg-${pkg.uuid}`}
                    itemId={`scenario-${scenario.id}-pkg-${pkg.uuid}`}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                        <Checkbox
                          size="small"
                          checked={selectedPackages.has(pkg.uuid)}
                          onClick={(e) => togglePackageSelection(pkg.uuid, e)}
                          sx={{ p: 0.5, mr: 0.5 }}
                        />
                        <PackageIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>{pkg.name}</Typography>
                      </Box>
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTreeItem({ type: 'package', id: pkg.uuid });
                    }}
                  />
                ))}
              </TreeItem>
            );
          })}
          {/* Unlinked packages group */}
          {filteredUnlinkedPackages.length > 0 && (
            <TreeItem
              itemId="unlinked-packages"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                  <UnlinkedIcon fontSize="small" sx={{ mr: 0.5, color: 'warning.main' }} />
                  <Typography variant="body2" fontWeight={500} color="warning.main">
                    Unlinked Packages ({filteredUnlinkedPackages.length})
                  </Typography>
                </Box>
              }
            >
              {filteredUnlinkedPackages.map(pkg => (
                <TreeItem
                  key={`unlinked-pkg-${pkg.uuid}`}
                  itemId={`unlinked-pkg-${pkg.uuid}`}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                      <Checkbox
                        size="small"
                        checked={selectedPackages.has(pkg.uuid)}
                        onClick={(e) => togglePackageSelection(pkg.uuid, e)}
                        sx={{ p: 0.5, mr: 0.5 }}
                      />
                      <PackageIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                      <Typography variant="body2" noWrap>{pkg.name}</Typography>
                    </Box>
                  }
                  onClick={() => setSelectedTreeItem({ type: 'package', id: pkg.uuid })}
                />
              ))}
            </TreeItem>
          )}
        </SimpleTreeView>
        <TablePagination
          component="div"
          count={filteredScenarios.length}
          page={scenarioTreePage}
          onPageChange={(_, p) => setScenarioTreePage(p)}
          rowsPerPage={scenarioTreeRowsPerPage}
          onRowsPerPageChange={(e) => { setScenarioTreeRowsPerPage(parseInt(e.target.value, 10)); setScenarioTreePage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      </>
    );
  };

  const renderByPackageTree = () => {
    const paginatedPackages = filteredPackages.slice(
      packageTreePage * packageTreeRowsPerPage,
      packageTreePage * packageTreeRowsPerPage + packageTreeRowsPerPage
    );
    return (
      <>
        <SimpleTreeView>
          {paginatedPackages.map(pkg => {
            const refs = packageToScenarios.get(pkg.uuid) || [];
            return (
              <TreeItem
                key={`package-${pkg.uuid}`}
                itemId={`package-${pkg.uuid}`}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={selectedPackages.has(pkg.uuid)}
                      onClick={(e) => togglePackageSelection(pkg.uuid, e)}
                      sx={{ p: 0.5, mr: 0.5 }}
                    />
                    <PackageIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {pkg.name}
                    </Typography>
                    {refs.length > 0 && (
                      <Chip label={`${refs.length} scenario`} size="small" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
                    )}
                  </Box>
                }
                onClick={() => setSelectedTreeItem({ type: 'package', id: pkg.uuid })}
              >
                {refs.map(scenario => (
                  <TreeItem
                    key={`package-${pkg.uuid}-scenario-${scenario.id}`}
                    itemId={`package-${pkg.uuid}-scenario-${scenario.id}`}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
                        <Checkbox
                          size="small"
                          checked={selectedScenarios.has(scenario.id)}
                          onClick={(e) => toggleScenarioSelection(scenario.id, e)}
                          sx={{ p: 0.5, mr: 0.5 }}
                        />
                        <ScenarioIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                        <Typography variant="body2" noWrap>{scenario.name}</Typography>
                      </Box>
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTreeItem({ type: 'scenario', id: scenario.id });
                    }}
                  />
                ))}
              </TreeItem>
            );
          })}
        </SimpleTreeView>
        <TablePagination
          component="div"
          count={filteredPackages.length}
          page={packageTreePage}
          onPageChange={(_, p) => setPackageTreePage(p)}
          rowsPerPage={packageTreeRowsPerPage}
          onRowsPerPageChange={(e) => { setPackageTreeRowsPerPage(parseInt(e.target.value, 10)); setPackageTreePage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: 1, borderColor: 'divider' }}
        />
      </>
    );
  };

  const renderS3FileTable = (
    files: S3File[],
    selectedFiles: Set<string>,
    toggleSelection: (key: string) => void,
    selectAll: () => void,
    filter: string,
    setFilter: (v: string) => void,
    sortField: 'name' | 'size' | 'lastModified',
    sortDirection: 'asc' | 'desc',
    handleSort: (field: 'name' | 'size' | 'lastModified') => void,
    page: number,
    setPage: (p: number) => void,
    rowsPerPage: number,
    setRowsPerPage: (r: number) => void,
  ) => (
    <>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Filter by file name..."
          value={filter}
          onChange={(e) => { setFilter(e.target.value); setPage(0); }}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => { setFilter(''); setPage(0); }}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedFiles.size === files.length && files.length > 0}
                  indeterminate={selectedFiles.size > 0 && selectedFiles.size < files.length}
                  onChange={selectAll}
                />
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'name'} direction={sortField === 'name' ? sortDirection : 'asc'} onClick={() => handleSort('name')}>
                  File Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'size'} direction={sortField === 'size' ? sortDirection : 'asc'} onClick={() => handleSort('size')}>
                  Size
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortField === 'lastModified'} direction={sortField === 'lastModified' ? sortDirection : 'asc'} onClick={() => handleSort('lastModified')}>
                  Last Modified
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((file) => (
              <TableRow key={file.key} selected={selectedFiles.has(file.key)} hover onClick={() => toggleSelection(file.key)} sx={{ cursor: 'pointer' }}>
                <TableCell padding="checkbox"><Checkbox checked={selectedFiles.has(file.key)} /></TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorageIcon fontSize="small" color="action" />
                    <Typography variant="body2">{file.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{formatBytes(file.size)}</Typography></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{new Date(file.lastModified).toLocaleString()}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={files.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>
    </>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <ScenarioIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Scenarios & Packages
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Manage scenarios and packages from RangeOS. Export to S3 or import from backups. Scenarios reference packages, and packages can include dependencies.
      </Typography>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
      )}

      {/* Environment Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Configuration</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>RangeOS Environment</InputLabel>
              <Select
                value={selectedEnvironment}
                onChange={(e) => setSelectedEnvironment(e.target.value)}
                label="RangeOS Environment"
                disabled={loading}
              >
                <MenuItem value=""><em>Select environment</em></MenuItem>
                {environments.map((env) => (
                  <MenuItem key={env.id} value={env.id}>{env.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>S3 Environment</InputLabel>
              <Select
                value={selectedS3Environment}
                onChange={(e) => setSelectedS3Environment(e.target.value)}
                label="S3 Environment"
                disabled={loading}
              >
                <MenuItem value=""><em>Select S3 environment</em></MenuItem>
                {s3Environments.map((env) => (
                  <MenuItem key={env.environmentId} value={env.prefix}>
                    {env.environmentName} ({env.bucketName})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
        <Tab
          label={`Export (${scenarioList.length} scenarios, ${packageList.length} packages)`}
          icon={<ExportIcon />}
          iconPosition="start"
        />
        <Tab
          label={`Import (${s3ScenarioFiles.length + s3PackageFiles.length} files)`}
          icon={<ImportIcon />}
          iconPosition="start"
        />
        <Tab
          label="Cleanup"
          icon={<CleanupIcon />}
          iconPosition="start"
        />
      </Tabs>

      {/* Export Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} />
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Tooltip title="Refresh">
                  <IconButton onClick={loadData} disabled={!selectedEnvironment || loading}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => { setExportTarget('local'); setExportDialogOpen(true); }}
                  disabled={totalSelected === 0 || exporting}
                  size="small"
                >
                  {exporting ? 'Exporting...' : `Download ${totalSelected}`}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<ExportIcon />}
                  onClick={() => { setExportTarget('s3'); setExportDialogOpen(true); }}
                  disabled={totalSelected === 0 || !selectedS3Environment || exporting}
                  size="small"
                >
                  {exporting ? 'Exporting...' : `Export ${totalSelected} to S3`}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={async () => {
                    setDeleteConfirmOpen(true);
                    setDeletePreview({});
                    if (deleteDependencies && selectedEnvironment) {
                      setLoadingPreview(true);
                      const previews: typeof deletePreview = {};
                      for (const uuid of Array.from(selectedPackages)) {
                        try {
                          previews[uuid] = await rangeosApiService.previewDeletePackageWithDependencies(selectedEnvironment, uuid);
                        } catch (e) { /* continue */ }
                      }
                      setDeletePreview(previews);
                      setLoadingPreview(false);
                    }
                  }}
                  disabled={selectedPackages.size === 0 || exporting || importing || deleting}
                  size="small"
                >
                  {deleting ? 'Deleting...' : `Delete ${selectedPackages.size} Pkg`}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={openScenarioDeleteConfirm}
                  disabled={selectedScenarios.size === 0 || exporting || importing || deleting}
                  size="small"
                >
                  {`Delete ${selectedScenarios.size} Scenario`}
                </Button>
              </Box>
            </Box>

            {!selectedEnvironment ? (
              <Alert severity="info">Select a RangeOS environment to view scenarios and packages</Alert>
            ) : scenarioList.length === 0 && packageList.length === 0 ? (
              <Alert severity="info">No scenarios or packages found in this environment</Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 2, minHeight: 500 }}>
                {/* Left Panel - TreeView */}
                <Box sx={{ width: '40%', minWidth: 300, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', pr: 2 }}>
                  {/* View mode toggle */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      onChange={(_, val) => val && setViewMode(val)}
                      size="small"
                    >
                      <ToggleButton value="by-scenario">By Scenario</ToggleButton>
                      <ToggleButton value="by-package">By Package</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Filter */}
                  <TextField
                    size="small"
                    placeholder="Filter..."
                    value={treeFilter}
                    onChange={(e) => { setTreeFilter(e.target.value); setScenarioTreePage(0); setPackageTreePage(0); }}
                    sx={{ mb: 1 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment>,
                      endAdornment: treeFilter ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setTreeFilter('')}><ClearIcon fontSize="small" /></IconButton>
                        </InputAdornment>
                      ) : undefined,
                    }}
                  />

                  {/* Selection summary */}
                  {totalSelected > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      {selectedScenarios.size > 0 && (
                        <Chip
                          label={`${selectedScenarios.size} scenario(s)`}
                          size="small"
                          color="primary"
                          onDelete={() => setSelectedScenarios(new Set())}
                        />
                      )}
                      {selectedPackages.size > 0 && (
                        <Chip
                          label={`${selectedPackages.size} package(s)`}
                          size="small"
                          color="secondary"
                          onDelete={() => setSelectedPackages(new Set())}
                        />
                      )}
                    </Box>
                  )}

                  {/* Tree */}
                  <Box sx={{ flex: 1, overflow: 'auto' }}>
                    {viewMode === 'by-scenario' ? renderByScenarioTree() : renderByPackageTree()}
                  </Box>
                </Box>

                {/* Right Panel - Detail */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {renderDetailPanel()}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Import Tab */}
      {activeTab === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Scenario Backups */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <ScenarioIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                  Scenario Backups in S3
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Tooltip title="Refresh">
                    <IconButton onClick={loadS3Files} disabled={!selectedS3Environment || loading} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={!selectedEnvironment || importing || validatingBundle}
                    size="small"
                  >
                    {validatingBundle ? 'Validating...' : 'Import Local File'}
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && selectedEnvironment) {
                          setPendingImportFile(file);
                          setImportDialogType('scenario');
                          setImportDialogSource('local');
                          setImportDialogOpen(true);
                          e.target.value = '';
                        }
                      }}
                    />
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ImportIcon />}
                    onClick={() => { setImportDialogType('scenario'); setImportDialogSource('s3'); setImportDialogOpen(true); }}
                    disabled={selectedS3ScenarioFiles.size === 0 || !selectedEnvironment || importing || validatingBundle}
                    size="small"
                  >
                    {validatingBundle ? 'Validating...' : importing ? 'Importing...' : `Import ${selectedS3ScenarioFiles.size} Scenario(s)`}
                  </Button>
                </Box>
              </Box>

              {!selectedS3Environment ? (
                <Alert severity="info">Select an S3 environment to view scenario backups</Alert>
              ) : s3ScenarioFiles.length === 0 ? (
                <Alert severity="info">No scenario backups found</Alert>
              ) : (
                renderS3FileTable(
                  filteredS3ScenarioFiles,
                  selectedS3ScenarioFiles,
                  toggleS3ScenarioFileSelection,
                  () => {
                    if (selectedS3ScenarioFiles.size === filteredS3ScenarioFiles.length) {
                      setSelectedS3ScenarioFiles(new Set());
                    } else {
                      setSelectedS3ScenarioFiles(new Set(filteredS3ScenarioFiles.map(f => f.key)));
                    }
                  },
                  s3ScenarioFilter,
                  setS3ScenarioFilter,
                  s3ScenarioSortField,
                  s3ScenarioSortDirection,
                  (field) => {
                    if (s3ScenarioSortField === field) {
                      setS3ScenarioSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                      setS3ScenarioSortField(field);
                      setS3ScenarioSortDirection('asc');
                    }
                  },
                  s3ScenarioPage,
                  setS3ScenarioPage,
                  s3ScenarioRowsPerPage,
                  setS3ScenarioRowsPerPage,
                )
              )}
            </CardContent>
          </Card>

          {/* Package Backups */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  <PackageIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
                  Package Backups in S3
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<UploadIcon />}
                    disabled={!selectedEnvironment || importing}
                    size="small"
                  >
                    Import Local File
                    <input
                      type="file"
                      hidden
                      accept=".json"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && selectedEnvironment) {
                          setPendingImportFile(file);
                          setImportDialogType('package');
                          setImportDialogSource('local');
                          setImportDialogOpen(true);
                          e.target.value = '';
                        }
                      }}
                    />
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<ImportIcon />}
                    onClick={() => { setImportDialogType('package'); setImportDialogSource('s3'); setImportDialogOpen(true); }}
                    disabled={selectedS3PackageFiles.size === 0 || !selectedEnvironment || importing}
                    size="small"
                  >
                    {importing ? 'Importing...' : `Import ${selectedS3PackageFiles.size} Package(s)`}
                  </Button>
                </Box>
              </Box>

              {!selectedS3Environment ? (
                <Alert severity="info">Select an S3 environment to view package backups</Alert>
              ) : s3PackageFiles.length === 0 ? (
                <Alert severity="info">No package backups found</Alert>
              ) : (
                renderS3FileTable(
                  filteredS3PackageFiles,
                  selectedS3PackageFiles,
                  toggleS3PackageFileSelection,
                  () => {
                    if (selectedS3PackageFiles.size === filteredS3PackageFiles.length) {
                      setSelectedS3PackageFiles(new Set());
                    } else {
                      setSelectedS3PackageFiles(new Set(filteredS3PackageFiles.map(f => f.key)));
                    }
                  },
                  s3PackageFilter,
                  setS3PackageFilter,
                  s3PackageSortField,
                  s3PackageSortDirection,
                  (field) => {
                    if (s3PackageSortField === field) {
                      setS3PackageSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                      setS3PackageSortField(field);
                      setS3PackageSortDirection('asc');
                    }
                  },
                  s3PackagePage,
                  setS3PackagePage,
                  s3PackageRowsPerPage,
                  setS3PackageRowsPerPage,
                )
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Cleanup Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            {/* Toolbar */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={scanForOrphans}
                disabled={!selectedEnvironment || loadingOrphans}
                startIcon={loadingOrphans ? <CircularProgress size={18} /> : <SearchIcon />}
              >
                {loadingOrphans ? 'Scanning...' : 'Scan for Orphans'}
              </Button>
              <FormControlLabel
                control={<Switch checked={deepScan} onChange={(e) => setDeepScan(e.target.checked)} size="small" />}
                label="Deep scan (all asset types)"
              />
              {totalSelectedOrphans > 0 && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={deleteSelectedOrphans}
                  disabled={deletingOrphans}
                  startIcon={deletingOrphans ? <CircularProgress size={18} /> : <DeleteIcon />}
                >
                  Delete {totalSelectedOrphans} Selected
                </Button>
              )}
            </Box>

            {!selectedEnvironment && (
              <Alert severity="info">Select a RangeOS environment to scan for orphaned objects.</Alert>
            )}

            {loadingOrphans && <LinearProgress sx={{ mb: 2 }} />}

            {orphanReport && (
              <Box>
                {/* Summary Chips */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${orphanReport.scenarios.orphaned.length} orphaned scenario${orphanReport.scenarios.orphaned.length !== 1 ? 's' : ''}`}
                    color={orphanReport.scenarios.orphaned.length > 0 ? 'warning' : 'success'}
                    size="small"
                  />
                  <Chip
                    label={`${orphanReport.packages.orphaned.length} orphaned package${orphanReport.packages.orphaned.length !== 1 ? 's' : ''}`}
                    color={orphanReport.packages.orphaned.length > 0 ? 'warning' : 'success'}
                    size="small"
                  />
                  <Chip
                    label={`${Object.values(orphanReport.assets).reduce((sum, r) => sum + r.orphaned.length, 0)} orphaned asset${Object.values(orphanReport.assets).reduce((sum, r) => sum + r.orphaned.length, 0) !== 1 ? 's' : ''}`}
                    color={Object.values(orphanReport.assets).reduce((sum, r) => sum + r.orphaned.length, 0) > 0 ? 'warning' : 'success'}
                    size="small"
                  />
                  <Chip
                    label={`${orphanReport.scenarios.total} scenarios, ${orphanReport.packages.total} packages total`}
                    variant="outlined"
                    size="small"
                  />
                </Box>

                {orphanReport.scenarios.orphaned.length === 0 && orphanReport.packages.orphaned.length === 0 &&
                  Object.values(orphanReport.assets).every(r => r.orphaned.length === 0) && (
                  <Alert severity="success" sx={{ mb: 2 }}>No orphaned objects found. Environment is clean.</Alert>
                )}

                {/* Orphaned Scenarios */}
                {orphanReport.scenarios.orphaned.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={selectedOrphanScenarios.size === orphanReport.scenarios.orphaned.length && orphanReport.scenarios.orphaned.length > 0}
                          indeterminate={selectedOrphanScenarios.size > 0 && selectedOrphanScenarios.size < orphanReport.scenarios.orphaned.length}
                          onChange={() => {
                            if (selectedOrphanScenarios.size === orphanReport.scenarios.orphaned.length) {
                              setSelectedOrphanScenarios(new Set());
                            } else {
                              setSelectedOrphanScenarios(new Set(orphanReport.scenarios.orphaned.map(s => s.uuid)));
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                        <ScenarioIcon fontSize="small" />
                        <Typography variant="subtitle1">
                          Orphaned Scenarios ({orphanReport.scenarios.orphaned.length} / {orphanReport.scenarios.total})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell>Name</TableCell>
                              <TableCell>Reason</TableCell>
                              <TableCell>UUID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orphanReport.scenarios.orphaned
                              .slice(orphanScenariosPage * orphanScenariosRowsPerPage, orphanScenariosPage * orphanScenariosRowsPerPage + orphanScenariosRowsPerPage)
                              .map(s => (
                              <TableRow key={s.uuid} hover>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedOrphanScenarios.has(s.uuid)}
                                    onChange={() => {
                                      setSelectedOrphanScenarios(prev => {
                                        const next = new Set(prev);
                                        if (next.has(s.uuid)) next.delete(s.uuid); else next.add(s.uuid);
                                        return next;
                                      });
                                    }}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{s.name}</TableCell>
                                <TableCell><Typography variant="body2" color="text.secondary">{s.reason}</Typography></TableCell>
                                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{s.uuid}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component="div"
                          count={orphanReport.scenarios.orphaned.length}
                          page={orphanScenariosPage}
                          onPageChange={(_, p) => setOrphanScenariosPage(p)}
                          rowsPerPage={orphanScenariosRowsPerPage}
                          onRowsPerPageChange={(e) => { setOrphanScenariosRowsPerPage(parseInt(e.target.value, 10)); setOrphanScenariosPage(0); }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Orphaned Packages */}
                {orphanReport.packages.orphaned.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={selectedOrphanPackages.size === orphanReport.packages.orphaned.length && orphanReport.packages.orphaned.length > 0}
                          indeterminate={selectedOrphanPackages.size > 0 && selectedOrphanPackages.size < orphanReport.packages.orphaned.length}
                          onChange={() => {
                            if (selectedOrphanPackages.size === orphanReport.packages.orphaned.length) {
                              setSelectedOrphanPackages(new Set());
                            } else {
                              setSelectedOrphanPackages(new Set(orphanReport.packages.orphaned.map(p => p.uuid)));
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                        <PackageIcon fontSize="small" />
                        <Typography variant="subtitle1">
                          Orphaned Packages ({orphanReport.packages.orphaned.length} / {orphanReport.packages.total})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell>Name</TableCell>
                              <TableCell>Reason</TableCell>
                              <TableCell>UUID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {orphanReport.packages.orphaned
                              .slice(orphanPackagesPage * orphanPackagesRowsPerPage, orphanPackagesPage * orphanPackagesRowsPerPage + orphanPackagesRowsPerPage)
                              .map(p => (
                              <TableRow key={p.uuid} hover>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedOrphanPackages.has(p.uuid)}
                                    onChange={() => {
                                      setSelectedOrphanPackages(prev => {
                                        const next = new Set(prev);
                                        if (next.has(p.uuid)) next.delete(p.uuid); else next.add(p.uuid);
                                        return next;
                                      });
                                    }}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{p.name}</TableCell>
                                <TableCell><Typography variant="body2" color="text.secondary">{p.reason}</Typography></TableCell>
                                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.uuid}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component="div"
                          count={orphanReport.packages.orphaned.length}
                          page={orphanPackagesPage}
                          onPageChange={(_, p) => setOrphanPackagesPage(p)}
                          rowsPerPage={orphanPackagesRowsPerPage}
                          onRowsPerPageChange={(e) => { setOrphanPackagesRowsPerPage(parseInt(e.target.value, 10)); setOrphanPackagesPage(0); }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Orphaned Assets by Type */}
                {Object.entries(orphanReport.assets)
                  .filter(([_, data]) => data.orphaned.length > 0)
                  .map(([assetType, data]) => (
                  <Accordion key={assetType}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Checkbox
                          checked={(selectedOrphanAssets.get(assetType)?.size || 0) === data.orphaned.length && data.orphaned.length > 0}
                          indeterminate={(selectedOrphanAssets.get(assetType)?.size || 0) > 0 && (selectedOrphanAssets.get(assetType)?.size || 0) < data.orphaned.length}
                          onChange={() => toggleAllOrphanAssets(assetType, data.orphaned.map(a => a.uuid))}
                          onClick={(e) => e.stopPropagation()}
                          size="small"
                        />
                        <StorageIcon fontSize="small" />
                        <Typography variant="subtitle1">
                          {ASSET_TYPE_LABELS[assetType] || assetType} ({data.orphaned.length} / {data.total})
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell>Name</TableCell>
                              <TableCell>UUID</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {data.orphaned
                              .slice(
                                (orphanAssetPages[assetType] || 0) * (orphanAssetRowsPerPage[assetType] || 25),
                                (orphanAssetPages[assetType] || 0) * (orphanAssetRowsPerPage[assetType] || 25) + (orphanAssetRowsPerPage[assetType] || 25)
                              )
                              .map(a => (
                              <TableRow key={a.uuid} hover>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    checked={selectedOrphanAssets.get(assetType)?.has(a.uuid) || false}
                                    onChange={() => toggleOrphanAsset(assetType, a.uuid)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>{a.name}</TableCell>
                                <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{a.uuid}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <TablePagination
                          component="div"
                          count={data.orphaned.length}
                          page={orphanAssetPages[assetType] || 0}
                          onPageChange={(_, p) => setOrphanAssetPages(prev => ({ ...prev, [assetType]: p }))}
                          rowsPerPage={orphanAssetRowsPerPage[assetType] || 25}
                          onRowsPerPageChange={(e) => {
                            const rpp = parseInt(e.target.value, 10);
                            setOrphanAssetRowsPerPage(prev => ({ ...prev, [assetType]: rpp }));
                            setOrphanAssetPages(prev => ({ ...prev, [assetType]: 0 }));
                          }}
                          rowsPerPageOptions={[10, 25, 50, 100]}
                        />
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Export Confirmation Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {exportTarget === 's3' ? 'Export to S3' : 'Download Export'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedScenarios.size > 0 && `${selectedScenarios.size} scenario(s)`}
            {selectedScenarios.size > 0 && selectedPackages.size > 0 && ' and '}
            {selectedPackages.size > 0 && `${selectedPackages.size} package(s)`}
            {' will be exported.'}
          </DialogContentText>

          {selectedPackages.size > 0 && (
            <FormControlLabel
              control={<Switch checked={includeDependencies} onChange={(e) => setIncludeDependencies(e.target.checked)} size="small" />}
              label="Include package dependencies (VM specs, networks, etc.)"
              sx={{ display: 'block', mb: 1 }}
            />
          )}
          {selectedScenarios.size > 0 && (
            <FormControlLabel
              control={<Switch checked={includeReferencedPackages} onChange={(e) => setIncludeReferencedPackages(e.target.checked)} size="small" />}
              label="Include referenced packages in scenario export"
              sx={{ display: 'block', mb: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmExport} startIcon={exportTarget === 's3' ? <ExportIcon /> : <DownloadIcon />}>
            {exportTarget === 's3' ? 'Export to S3' : 'Download'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Confirmation Dialog */}
      <Dialog open={importDialogOpen} onClose={() => { setImportDialogOpen(false); setPendingImportFile(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Import {importDialogType === 'scenario' ? 'Scenario' : 'Package'}{importDialogType === 'scenario' && selectedS3ScenarioFiles.size > 1 ? 's' : importDialogType === 'package' && selectedS3PackageFiles.size > 1 ? 's' : ''}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {importDialogSource === 'local'
              ? `Import from local file: ${pendingImportFile?.name || 'unknown'}`
              : importDialogType === 'scenario'
                ? `${selectedS3ScenarioFiles.size} scenario file(s) selected from S3.`
                : `${selectedS3PackageFiles.size} package file(s) selected from S3.`
            }
          </DialogContentText>

          {importDialogType === 'scenario' && (
            <FormControlLabel
              control={<Switch checked={importReferencedPackages} onChange={(e) => setImportReferencedPackages(e.target.checked)} size="small" />}
              label="Import referenced packages"
              sx={{ display: 'block', mb: 1 }}
            />
          )}
          {importDialogType === 'package' && (
            <FormControlLabel
              control={<Switch checked={importDependencies} onChange={(e) => setImportDependencies(e.target.checked)} size="small" />}
              label="Import dependencies (VM specs, networks, etc.)"
              sx={{ display: 'block', mb: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setImportDialogOpen(false); setPendingImportFile(null); }}>Cancel</Button>
          <Button variant="contained" onClick={confirmImport} startIcon={<ImportIcon />}>
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Delete Packages</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedPackages.size} package(s)? This action cannot be undone.
          </DialogContentText>

          <List dense>
            {Array.from(selectedPackages).map(uuid => {
              const pkg = packageList.find(p => p.uuid === uuid);
              const preview = deletePreview[uuid];
              return (
                <ListItem key={uuid}>
                  <ListItemText
                    primary={pkg?.name || uuid}
                    secondary={pkg?.version ? `v${pkg.version}` : undefined}
                  />
                  {deleteDependencies && preview && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Chip size="small" label={`${preview.willDelete.length} to delete`} color="error" variant="outlined" />
                      {preview.willSkipShared.length > 0 && (
                        <Chip size="small" label={`${preview.willSkipShared.length} shared (kept)`} color="warning" variant="outlined" />
                      )}
                    </Box>
                  )}
                </ListItem>
              );
            })}
          </List>

          <FormControlLabel
            control={
              <Switch
                checked={deleteDependencies}
                onChange={(e) => {
                  setDeleteDependencies(e.target.checked);
                  if (e.target.checked && selectedEnvironment && Object.keys(deletePreview).length === 0) {
                    setLoadingPreview(true);
                    const fetchPreviews = async () => {
                      const previews: typeof deletePreview = {};
                      for (const uuid of Array.from(selectedPackages)) {
                        try {
                          previews[uuid] = await rangeosApiService.previewDeletePackageWithDependencies(selectedEnvironment, uuid);
                        } catch (e) { /* continue */ }
                      }
                      setDeletePreview(previews);
                      setLoadingPreview(false);
                    };
                    fetchPreviews();
                  }
                }}
                size="small"
              />
            }
            label="Delete dependencies"
          />

          {loadingPreview && <LinearProgress sx={{ mt: 1 }} />}

          {deleteDependencies && !loadingPreview && Object.keys(deletePreview).length > 0 && (
            <Box sx={{ mt: 1 }}>
              {Object.values(deletePreview).some(p => p.willDelete.length > 0) && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Will be deleted:</Typography>
                  {Object.entries(deletePreview).map(([uuid, preview]) =>
                    preview.willDelete.map(dep => (
                      <Typography key={`${uuid}-${dep.uuid}`} variant="body2">
                        {dep.name || dep.uuid} ({dep.fieldName})
                      </Typography>
                    ))
                  )}
                </Alert>
              )}

              {Object.values(deletePreview).some(p => p.willSkipShared.length > 0) && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Shared dependencies (will be preserved):</Typography>
                  {Object.entries(deletePreview).map(([uuid, preview]) =>
                    preview.willSkipShared.map(dep => (
                      <Typography key={`${uuid}-${dep.uuid}`} variant="body2">
                        {dep.name || dep.uuid} ({dep.fieldName}) — shared with: {dep.sharedWith.join(', ')}
                      </Typography>
                    ))
                  )}
                </Alert>
              )}

              {Object.values(deletePreview).every(p => p.totalDependencies === 0) && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No dependencies found for the selected package(s).
                </Alert>
              )}
            </Box>
          )}

          {deleteDependencies && !loadingPreview && Object.keys(deletePreview).length === 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Dependencies shared with other packages will be preserved.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeletePackages} color="error" variant="contained" disabled={loadingPreview}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scenario Delete Confirmation Dialog */}
      <Dialog open={scenarioDeleteConfirmOpen} onClose={() => setScenarioDeleteConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Delete Scenario{selectedScenarios.size > 1 ? 's' : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedScenarios.size} scenario(s)? This action cannot be undone.
          </DialogContentText>

          <List dense>
            {Array.from(selectedScenarios).map(uuid => {
              const scenario = scenarioList.find((s: any) => s.id === uuid || s.uuid === uuid);
              const preview = scenarioDeletePreview[uuid];
              return (
                <ListItem key={uuid}>
                  <ListItemText
                    primary={scenario?.name || uuid}
                    secondary={preview ? `${preview.packages.length} package(s) referenced` : undefined}
                  />
                </ListItem>
              );
            })}
          </List>

          <FormControlLabel
            control={
              <Switch
                checked={scenarioDeleteOrphanedPackages}
                onChange={(e) => setScenarioDeleteOrphanedPackages(e.target.checked)}
                size="small"
              />
            }
            label="Delete orphaned packages (not referenced by other scenarios)"
          />

          {loadingScenarioDeletePreview && <LinearProgress sx={{ mt: 1 }} />}

          {!loadingScenarioDeletePreview && Object.keys(scenarioDeletePreview).length > 0 && (
            <Box sx={{ mt: 1 }}>
              {Object.entries(scenarioDeletePreview).map(([uuid, preview]) => {
                const orphaned = preview.packages.filter(p => p.isOrphaned);
                const shared = preview.packages.filter(p => !p.isOrphaned);
                return (
                  <Box key={uuid} sx={{ mb: 1 }}>
                    {preview.packages.length === 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Scenario "{preview.scenarioName}" has no packages.
                      </Alert>
                    )}
                    {orphaned.length > 0 && (
                      <Alert severity={scenarioDeleteOrphanedPackages ? 'error' : 'info'} sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {scenarioDeleteOrphanedPackages ? 'Will be deleted (orphaned):' : 'Orphaned packages (will be kept):'}
                        </Typography>
                        {orphaned.map(pkg => (
                          <Typography key={pkg.uuid} variant="body2">
                            {pkg.name}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                    {shared.length > 0 && (
                      <Alert severity="warning" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>Shared packages (will be preserved):</Typography>
                        {shared.map(pkg => (
                          <Typography key={pkg.uuid} variant="body2">
                            {pkg.name} — also used by: {pkg.otherScenarios.join(', ')}
                          </Typography>
                        ))}
                      </Alert>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScenarioDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteScenarios} color="error" variant="contained" disabled={loadingScenarioDeletePreview}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Scenario Bundle Import Validation Dialog */}
      <Dialog
        open={showImportValidationDialog}
        onClose={() => {
          setShowImportValidationDialog(false);
          setPendingImportData(null);
          setImportValidationReport(null);
          setImportUuidMappings(new Map());
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Scenario Import Validation — {importValidationReport?.scenarioName || 'Unknown'}
        </DialogTitle>
        <DialogContent>
          {importValidationReport && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>{importValidationReport.summary.missingReferences}</strong> of {importValidationReport.summary.totalReferences} referenced assets
                  are missing in the target environment. Resolve them below or import with dangling references.
                </Typography>
              </Alert>

              {Object.entries(importValidationReport.details.byType).map(([type, typeDetails]) => {
                const missingRefs = typeDetails.references.filter((d: ReferenceValidationDetail) => !d.exists);
                if (missingRefs.length === 0 && typeDetails.validCount > 0) {
                  return (
                    <Box key={type} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                        {type} — {typeDetails.validCount} / {typeDetails.uniqueCount} valid
                      </Typography>
                    </Box>
                  );
                }
                if (typeDetails.uniqueCount === 0) return null;

                return (
                  <Accordion key={type} defaultExpanded={missingRefs.length > 0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        {missingRefs.length > 0 ? (
                          <ErrorIcon color="error" fontSize="small" />
                        ) : (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          {type}
                        </Typography>
                        <Chip
                          label={`${typeDetails.validCount} / ${typeDetails.uniqueCount} valid`}
                          color={missingRefs.length > 0 ? 'error' : 'success'}
                          size="small"
                        />
                        {importUuidMappings.size > 0 && (() => {
                          const mappedCount = missingRefs.filter((d: ReferenceValidationDetail) => importUuidMappings.has(d.uuid)).length;
                          return mappedCount > 0 ? (
                            <Chip label={`${mappedCount} mapped`} color="warning" size="small" />
                          ) : null;
                        })()}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width={40}>Status</TableCell>
                              <TableCell>UUID</TableCell>
                              <TableCell>Resource Name</TableCell>
                              <TableCell>Used By</TableCell>
                              <TableCell>Best Match</TableCell>
                              <TableCell width={80}>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {typeDetails.references.map((detail: ReferenceValidationDetail) => {
                              const hasMapping = importUuidMappings.has(detail.uuid);
                              const mapping = importUuidMappings.get(detail.uuid);
                              const bestMatch = detail.suggestedMatches?.[0];

                              return (
                                <TableRow key={detail.uuid}>
                                  <TableCell>
                                    {detail.exists ? (
                                      <CheckCircleIcon color="success" fontSize="small" />
                                    ) : hasMapping ? (
                                      <WarningIcon color="warning" fontSize="small" />
                                    ) : (
                                      <ErrorIcon color="error" fontSize="small" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                      {detail.uuid.substring(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {detail.exists ? (
                                      detail.resourceName
                                    ) : hasMapping ? (
                                      <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                          {detail.resourceName || 'Missing'}
                                        </Typography>
                                        <Typography variant="body2" color="warning.main">
                                          → {mapping?.resourceName || mapping?.newUuid.substring(0, 8) + '...'}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      <Typography variant="body2" color="error">
                                        {detail.resourceName && detail.resourceName !== detail.uuid
                                          ? detail.resourceName
                                          : `Unknown (${detail.uuid.substring(0, 8)}...)`}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {detail.usedBy && (
                                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                        {detail.usedBy}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {!detail.exists && hasMapping && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'warning.main' }}>
                                            {mapping?.resourceName || 'Mapped'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {mapping?.newUuid.substring(0, 8)}...
                                          </Typography>
                                        </Box>
                                        <Chip label="mapped" color="warning" size="small" />
                                      </Box>
                                    )}
                                    {!detail.exists && !hasMapping && bestMatch && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box>
                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {bestMatch.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                            {bestMatch.matchReason}
                                          </Typography>
                                        </Box>
                                        <Chip
                                          label={bestMatch.confidence}
                                          color={getConfidenceColor(bestMatch.confidence) as any}
                                          size="small"
                                        />
                                      </Box>
                                    )}
                                    {!detail.exists && !hasMapping && !bestMatch && (
                                      <Typography variant="caption" color="text.secondary">No matches</Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    {!detail.exists && !hasMapping && (
                                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        {bestMatch && (
                                          <Tooltip title="Accept this match">
                                            <IconButton
                                              size="small"
                                              color="success"
                                              onClick={() => {
                                                const newMappings = new Map(importUuidMappings);
                                                newMappings.set(detail.uuid, {
                                                  oldUuid: detail.uuid,
                                                  newUuid: bestMatch.uuid,
                                                  type: detail.type,
                                                  resourceName: bestMatch.name,
                                                });
                                                setImportUuidMappings(newMappings);
                                              }}
                                            >
                                              <CheckCircleIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        <Tooltip title={bestMatch ? "View all matches" : "Browse & Map Resource"}>
                                          <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => openImportMatchDialog(detail)}
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    )}
                                    {!detail.exists && hasMapping && (
                                      <Tooltip title="Clear mapping">
                                        <IconButton
                                          size="small"
                                          color="warning"
                                          onClick={() => {
                                            const newMappings = new Map(importUuidMappings);
                                            newMappings.delete(detail.uuid);
                                            setImportUuidMappings(newMappings);
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {detail.exists && (
                                      <Typography variant="caption" color="text.secondary">-</Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined"
              color="success"
              onClick={autoMapHighConfidenceImport}
              startIcon={<CheckCircleIcon />}
              size="small"
            >
              Auto-Map High Confidence
            </Button>
            <FormControlLabel
              control={<Switch checked={importReferencedPackages} onChange={(e) => setImportReferencedPackages(e.target.checked)} size="small" />}
              label={
                <Typography variant="body2">
                  Import Packages{pendingImportData?.packageCount
                    ? ` (${pendingImportData.packageCount})`
                    : ''}
                </Typography>
              }
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={() => {
              setShowImportValidationDialog(false);
              setPendingImportData(null);
              setImportValidationReport(null);
              setImportUuidMappings(new Map());
            }}>
              Cancel
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={handleImportAnyway}
              disabled={importing}
            >
              Import Anyway
            </Button>
            <Button
              variant="contained"
              onClick={handleImportWithMappings}
              disabled={importing || importUuidMappings.size === 0}
            >
              {importing ? 'Importing...' : `Import with ${importUuidMappings.size} Mapping(s)`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Import Match Selection Dialog */}
      <Dialog open={importMatchDialogOpen} onClose={() => setImportMatchDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Select Replacement for {importCurrentMatch?.detail.type}
        </DialogTitle>
        <DialogContent>
          {importCurrentMatch && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Original UUID:</strong> {importCurrentMatch.detail.uuid}
                </Typography>
                {importCurrentMatch.detail.resourceName && importCurrentMatch.detail.resourceName !== importCurrentMatch.detail.uuid && (
                  <Typography variant="body2">
                    <strong>Original Name:</strong> {importCurrentMatch.detail.resourceName}
                  </Typography>
                )}
              </Alert>

              {/* Suggested Matches section */}
              {importCurrentMatch.detail.suggestedMatches && importCurrentMatch.detail.suggestedMatches.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>Suggested Matches:</Typography>
                  <RadioGroup value={importSelectedMatchUuid} onChange={(e) => { setImportSelectedMatchUuid(e.target.value); setImportManualUuidInput(''); }}>
                    {importCurrentMatch.detail.suggestedMatches.map((match: SuggestedMatch) => (
                      <Paper key={match.uuid} variant="outlined" sx={{ p: 2, mb: 1 }}>
                        <FormControlLabel
                          value={match.uuid}
                          control={<Radio />}
                          label={
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body1">{match.name}</Typography>
                                <Chip
                                  label={match.confidence}
                                  color={getConfidenceColor(match.confidence) as any}
                                  size="small"
                                />
                              </Box>
                              <Typography variant="body2" color="text.secondary">UUID: {match.uuid}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {match.matchReason}
                              </Typography>
                            </Box>
                          }
                        />
                      </Paper>
                    ))}
                  </RadioGroup>
                </>
              )}

              {/* Browse All Resources section */}
              {importCurrentMatch.detail.suggestedMatches && importCurrentMatch.detail.suggestedMatches.length > 0 && !showAllResources ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowAllResources(true)}
                  sx={{ mt: 1, mb: 1 }}
                >
                  Browse all {importCurrentMatch.detail.type} resources...
                </Button>
              ) : (
                <Box sx={{ mt: importCurrentMatch.detail.suggestedMatches?.length ? 2 : 0 }}>
                  {(!importCurrentMatch.detail.suggestedMatches || importCurrentMatch.detail.suggestedMatches.length === 0) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      No suggested matches found. Search available resources below.
                    </Alert>
                  )}
                  <Typography variant="subtitle2" gutterBottom>
                    All {importCurrentMatch.detail.type} Resources in Target Environment:
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={`Search ${importCurrentMatch.detail.type} by name...`}
                    value={importResourceSearch}
                    onChange={(e) => setImportResourceSearch(e.target.value)}
                    sx={{ mb: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: importResourceSearch ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setImportResourceSearch('')}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  {importLoadingResources ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2">Loading available resources...</Typography>
                    </Box>
                  ) : importAvailableResources.length > 0 ? (
                    <Paper variant="outlined" sx={{ maxHeight: 350, overflow: 'auto' }}>
                      <RadioGroup value={importSelectedMatchUuid} onChange={(e) => { setImportSelectedMatchUuid(e.target.value); setImportManualUuidInput(''); }}>
                        {importAvailableResources
                          .filter((resource) => {
                            if (!importResourceSearch.trim()) return true;
                            const search = importResourceSearch.toLowerCase();
                            const name = (resource.name || resource.displayName || resource.title || '').toLowerCase();
                            const uuid = (resource.uuid || '').toLowerCase();
                            return name.includes(search) || uuid.includes(search);
                          })
                          .map((resource) => (
                            <Paper key={resource.uuid} variant="outlined" sx={{ p: 1.5, m: 1 }}>
                              <FormControlLabel
                                value={resource.uuid}
                                control={<Radio />}
                                label={
                                  <Box>
                                    <Typography variant="body2">{resource.name || resource.displayName || resource.title || 'Unnamed'}</Typography>
                                    <Typography variant="caption" color="text.secondary">{resource.uuid}</Typography>
                                  </Box>
                                }
                              />
                            </Paper>
                          ))}
                      </RadioGroup>
                      {importAvailableResources.length > 0 && importResourceSearch.trim() &&
                        importAvailableResources.filter((r) => {
                          const search = importResourceSearch.toLowerCase();
                          const name = (r.name || r.displayName || r.title || '').toLowerCase();
                          return name.includes(search) || (r.uuid || '').toLowerCase().includes(search);
                        }).length === 0 && (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                            No resources match "{importResourceSearch}"
                          </Typography>
                        )}
                    </Paper>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No {importCurrentMatch.detail.type} resources found in target environment.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportMatchDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={confirmImportMatch}
            disabled={!importSelectedMatchUuid}
          >
            Confirm Match
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
